import { useMemo } from "react";
import { findSiblingSupportArticlesInCategory } from "../utils";
import type { Article, Category } from "../support.types";

export type UseArticleNavigationArgs = {
  /** The current article ID */
  articleId: string | null;
  /** Categories to search for siblings */
  categories: Category[];
};

export type UseArticleNavigationResult = {
  prev: Article | null;
  next: Article | null;
};

/**
 * Finds the previous and next articles within the same category.
 *
 * @example
 * ```typescript
 * const { data } = useFetchSupportArticles();
 * const { prev, next } = useArticleNavigation({
 *   articleId: currentArticleId,
 *   categories: data?.categories ?? [],
 * });
 * ```
 */
export function useArticleNavigation({
  articleId,
  categories,
}: UseArticleNavigationArgs): UseArticleNavigationResult {
  return useMemo(() => {
    if (!articleId || categories.length === 0) {
      return { prev: null, next: null };
    }

    const { prev, next } = findSiblingSupportArticlesInCategory(
      categories,
      articleId
    );

    return {
      prev: prev ?? null,
      next: next ?? null,
    };
  }, [articleId, categories]);
}

export default useArticleNavigation;

